// ─── PrivacyPolicy.jsx ────────────────────────────────────────────────────────
// Route: /privacy
// Add to App.jsx:
//   import PrivacyPolicy from './components/legal/PrivacyPolicy';
//   <Route path="/privacy" element={<PrivacyPolicy />} />
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Logo } from "../Logo";

const LAST_UPDATED = "June 2025"; // update when you make changes

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 36 }}>
    <h2 style={{
      fontSize: 16, fontWeight: 800, color: "#f8fafc",
      margin: "0 0 12px", letterSpacing: "-0.01em",
      paddingBottom: 10, borderBottom: "1px solid #1e1e1e",
    }}>{title}</h2>
    {children}
  </div>
);

const P = ({ children }) => (
  <p style={{ fontSize: 13, color: "#64748b", lineHeight: 1.85, marginBottom: 12 }}>{children}</p>
);

const UL = ({ items }) => (
  <ul style={{ paddingLeft: 18, marginBottom: 12 }}>
    {items.map((item, i) => (
      <li key={i} style={{ fontSize: 13, color: "#64748b", lineHeight: 1.85, marginBottom: 4 }}>{item}</li>
    ))}
  </ul>
);

const Highlight = ({ children }) => (
  <div style={{
    background: "#111", border: "1px solid #1e1e1e",
    borderRadius: 10, padding: "14px 18px", marginBottom: 16,
    borderLeft: "3px solid #f97316",
  }}>
    {children}
  </div>
);

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Privacy Policy — 100ViewsOnly";
  }, []);

  return (
    <div style={{
      background: "#0a0a0a", minHeight: "100vh",
      fontFamily: "'Inter', system-ui, sans-serif", color: "#e2e8f0",
    }}>
      {/* Nav */}
      <div style={{
        background: "#0a0a0a", borderBottom: "1px solid #1e1e1e",
        padding: "14px 24px", display: "flex", alignItems: "center",
        justifyContent: "space-between", position: "sticky", top: 0, zIndex: 10,
      }}>
        <span onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
          <Logo dark />
        </span>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: "none", border: "1px solid #1e1e1e",
            borderRadius: 20, padding: "5px 14px",
            color: "#475569", fontSize: 11, fontWeight: 600,
            cursor: "pointer", fontFamily: "inherit",
          }}
        >
          ← Back
        </button>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "48px 24px 80px" }}>

        {/* Header */}
        <div style={{ marginBottom: 40 }}>
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: "0.18em",
            color: "#f97316", textTransform: "uppercase", display: "block", marginBottom: 10,
          }}>Legal</span>
          <h1 style={{
            fontSize: 32, fontWeight: 900, color: "#fff",
            letterSpacing: "-0.02em", margin: "0 0 8px", lineHeight: 1.1,
          }}>Privacy Policy</h1>
          <p style={{ fontSize: 12, color: "#334155", margin: 0 }}>
            Last updated: {LAST_UPDATED} · Governed by PIPEDA (Canada's federal privacy law)
          </p>
        </div>

        {/* Plain English callout */}
        <Highlight>
          <p style={{ fontSize: 13, color: "#94a3b8", margin: 0, lineHeight: 1.7 }}>
            <strong style={{ color: "#fff" }}>Plain English summary:</strong> We collect your name, email, and content preferences to run the 100ViewsOnly platform. We never sell your data. You can ask us to delete everything at any time. That's the whole story.
          </p>
        </Highlight>

        <Section title="1. Who We Are">
          <P>100ViewsOnly is a Canadian creator monetization platform operated by Viktor, operating as 100ViewsOnly, based in Parkhill, Ontario, Canada. <em style={{ color: "#334155" }}>(This will be updated with the registered business name upon completion of Ontario sole proprietor registration.)</em></P>
          <P>For privacy questions: <a href="mailto:privacy@100viewsonly.ca" style={{ color: "#a78bfa" }}>privacy@100viewsonly.ca</a></P>
        </Section>

        <Section title="2. What Information We Collect">
          <p style={{ fontSize: 12, fontWeight: 700, color: "#94a3b8", margin: "0 0 8px", textTransform: "uppercase", letterSpacing: "0.08em" }}>When you join the waitlist</p>
          <UL items={[
            "Your name and email address",
            "Content niches you selected",
            "Whether you joined as a fan or creator",
            "The referral source if you clicked a creator's link",
            "Your IP address and the date/time of signup (required for CASL compliance)",
            "Your explicit consent record — the exact text shown and when you agreed",
          ]} />
          <p style={{ fontSize: 12, fontWeight: 700, color: "#94a3b8", margin: "0 0 8px", textTransform: "uppercase", letterSpacing: "0.08em" }}>When you create an account</p>
          <UL items={[
            "Profile information (name, username, bio, profile photo)",
            "Payment information processed securely by Stripe (we never see your full card or bank details)",
            "Content you upload to the platform",
            "Your activity on the platform (views, likes, saves)",
          ]} />
          <p style={{ fontSize: 12, fontWeight: 700, color: "#94a3b8", margin: "0 0 8px", textTransform: "uppercase", letterSpacing: "0.08em" }}>Automatically collected</p>
          <UL items={[
            "Browser type, device type, and operating system",
            "Pages visited and time spent on the platform",
            "General location (country/province level — not precise location)",
          ]} />
        </Section>

        <Section title="3. Why We Collect It">
          <UL items={[
            "Waitlist management — to notify you when your spot is ready",
            "Platform operation — to run your account and process payments",
            "Platform communications — to send updates you consented to receive",
            "Legal compliance — to maintain CASL consent records for 3 years as required by law",
            "Platform improvement — to understand how people use the platform",
            "Fraud prevention — to keep the platform safe for creators and fans",
          ]} />
        </Section>

        <Section title="4. What We Never Do">
          <Highlight>
            <UL items={[
              "We never sell your personal information to any third party",
              "We never rent your email list to advertisers or marketers",
              "We never use your data for advertising targeting on other platforms",
              "We never share your referral data with parties outside 100ViewsOnly",
            ]} />
          </Highlight>
        </Section>

        <Section title="5. Who We Share Data With">
          <P>We share limited data only with the following trusted service providers, solely to operate the platform:</P>
          <UL items={[
            "Firebase / Google Cloud — database and file storage, servers in Canada and the US",
            "Stripe — payment processing. Stripe handles all payment data under their own privacy policy and PCI DSS compliance",
            "Resend — email delivery. Only your email address and name are shared to send platform emails",
          ]} />
          <P>All third-party providers are contractually required to protect your data and may not use it for their own purposes.</P>
        </Section>

        <Section title="6. How Long We Keep Your Data">
          <UL items={[
            "Waitlist records — kept for 3 years after your last interaction as required by CASL",
            "Account data — kept for the duration of your account plus 2 years after closure",
            "Payment records — kept for 7 years as required by Canadian tax law",
            "Deleted accounts — anonymized within 30 days of deletion request, except where legally required",
          ]} />
        </Section>

        <Section title="7. Your Rights Under PIPEDA">
          <P>As a Canadian resident you have the right to:</P>
          <UL items={[
            "Access — request a copy of all personal information we hold about you",
            "Correction — request correction of inaccurate information",
            "Deletion — request deletion of your data (subject to legal retention requirements)",
            "Withdrawal of consent — withdraw consent to marketing at any time via our unsubscribe page",
            "Complaint — file a complaint with the Office of the Privacy Commissioner of Canada at priv.gc.ca",
          ]} />
          <P>To exercise any of these rights email <a href="mailto:privacy@100viewsonly.ca" style={{ color: "#a78bfa" }}>privacy@100viewsonly.ca</a>. We will respond within 30 days.</P>
        </Section>

        <Section title="8. Cookies">
          <P>We use essential cookies only — to keep you logged in and remember your preferences. We do not use advertising or tracking cookies. You can disable cookies in your browser settings, though some platform features may not work correctly.</P>
        </Section>

        <Section title="9. Data Security">
          <P>Your data is stored securely using Firebase (Google Cloud) with industry-standard encryption in transit and at rest. Access to personal data is restricted to authorized personnel only. In the event of a data breach that poses a real risk of significant harm, we will notify affected users and the Office of the Privacy Commissioner as required by PIPEDA.</P>
        </Section>

        <Section title="10. Changes to This Policy">
          <P>We may update this policy as the platform grows. We will notify you of significant changes by email and update the "Last updated" date above. Continued use of the platform after notification constitutes acceptance of the updated policy.</P>
        </Section>

        <Section title="11. Contact">
          <P>
            Privacy questions: <a href="mailto:privacy@100viewsonly.ca" style={{ color: "#a78bfa" }}>privacy@100viewsonly.ca</a><br />
            General support: <a href="mailto:support@100viewsonly.ca" style={{ color: "#a78bfa" }}>support@100viewsonly.ca</a><br />
            100ViewsOnly · Parkhill, Ontario, Canada
          </P>
        </Section>

      </div>
    </div>
  );
}
